import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { useScope } from "@/hooks/use-scope";
import {
  listDeliveryCommitmentRules,
  upsertDeliveryCommitmentRule,
  deleteDeliveryCommitmentRule,
  previewDeliveryCommitmentPolicy,
} from "@/lib/delivery-commitment-rules.functions";
import { runServerFn } from "@/lib/server-fn";

export const Route = createFileRoute("/_authenticated/app/admin/delivery-commitment-policies")({
  component: DeliveryCommitmentPoliciesAdmin,
});

type EnforcementMode = "off" | "warn" | "block" | "auto_upgrade";
type CommitmentSource = "customer_required_by" | "channel_promised_by" | "none";

function DeliveryCommitmentPoliciesAdmin() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listDeliveryCommitmentRules);
  const upsertFn = useServerFn(upsertDeliveryCommitmentRule);
  const deleteFn = useServerFn(deleteDeliveryCommitmentRule);
  const previewFn = useServerFn(previewDeliveryCommitmentPolicy);

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["dc-policies", tenantId],
    queryFn: () => listFn(),
    enabled: !!tenantId,
  });

  const [draft, setDraft] = useState({
    channel: "",
    customerId: "",
    serviceLevel: "",
    facilityIdInput: "",
    commitment_source: "customer_required_by" as CommitmentSource,
    enforcement_mode: "warn" as EnforcementMode,
    grace_hours: "0",
    auto_upgrade_to_service: "",
    notify_on_miss: true,
    is_active: true,
    notes: "",
  });

  const save = useMutation({
    mutationFn: () =>
      runServerFn(
        () =>
          upsertFn({
            data: {
              tenant_id: tenantId!,
              channel: draft.channel || null,
              customer_id: draft.customerId || null,
              service_level: draft.serviceLevel || null,
              facility_id: draft.facilityIdInput || null,
              commitment_source: draft.commitment_source,
              enforcement_mode: draft.enforcement_mode,
              grace_hours: Number(draft.grace_hours) || 0,
              auto_upgrade_to_service: draft.auto_upgrade_to_service || null,
              notify_on_miss: draft.notify_on_miss,
              is_active: draft.is_active,
              notes: draft.notes || null,
            },
          }),
        "Saving commitment policy",
      ),
    onSuccess: () => {
      toast.success("Commitment policy saved");
      qc.invalidateQueries({ queryKey: ["dc-policies", tenantId] });
      qc.invalidateQueries({ queryKey: ["dc-preview"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      runServerFn(() => deleteFn({ data: { id } }), "Deleting policy"),
    onSuccess: () => {
      toast.success("Policy removed");
      qc.invalidateQueries({ queryKey: ["dc-policies", tenantId] });
    },
  });

  const [previewScope, setPreviewScope] = useState({
    channel: "",
    customerId: "",
    serviceLevel: "",
    facilityId: facilityId ?? "",
  });
  const { data: preview } = useQuery({
    queryKey: ["dc-preview", tenantId, previewScope],
    queryFn: () =>
      previewFn({
        data: {
          tenantId: tenantId!,
          channel: previewScope.channel || null,
          customerId: previewScope.customerId || null,
          serviceLevel: previewScope.serviceLevel || null,
          facilityId: previewScope.facilityId || null,
        },
      }),
    enabled: !!tenantId,
  });

  if (!tenantId) {
    return (
      <div className="p-8 text-sm text-muted-foreground">
        Select a tenant to manage delivery commitment policies.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <PageHeader
        title="Delivery Commitment Policies"
        description="Enforce Arrival Date commitments across rate shop, wave release, and pack/ship. Scope cascades: Channel → Customer → Service Level → Facility → Tenant Default. Most-specific rule wins."
      />

      <Card className="p-4 space-y-4">
        <div className="font-medium">Cascade preview</div>
        <div className="grid grid-cols-4 gap-3">
          <ScopeInput label="Channel" value={previewScope.channel}
            onChange={(v) => setPreviewScope({ ...previewScope, channel: v })}
            placeholder="shopify" />
          <ScopeInput label="Customer ID" value={previewScope.customerId}
            onChange={(v) => setPreviewScope({ ...previewScope, customerId: v })}
            placeholder="uuid" />
          <ScopeInput label="Service level" value={previewScope.serviceLevel}
            onChange={(v) => setPreviewScope({ ...previewScope, serviceLevel: v })}
            placeholder="2_day" />
          <ScopeInput label="Facility ID" value={previewScope.facilityId}
            onChange={(v) => setPreviewScope({ ...previewScope, facilityId: v })}
            placeholder="uuid" />
        </div>
        {preview && (
          <div className="border-t pt-3 text-sm space-y-2">
            {preview.winner ? (
              <>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{preview.provenance?.label}</Badge>
                  <span className="text-muted-foreground">
                    enforcement: <span className="font-medium text-foreground">{preview.winner.enforcement_mode}</span>
                    {" · "}grace: {preview.winner.grace_hours}h
                    {preview.winner.auto_upgrade_to_service
                      ? ` · auto-upgrade → ${preview.winner.auto_upgrade_to_service}`
                      : ""}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Matches {preview.matchingOpenOrders} open order
                  {preview.matchingOpenOrders === 1 ? "" : "s"} at this scope.
                  {preview.runnersUp.length > 0 && (
                    <> {preview.runnersUp.length} less-specific rule
                      {preview.runnersUp.length === 1 ? "" : "s"} would apply if this rule were removed.</>
                  )}
                </div>
              </>
            ) : (
              <div className="text-muted-foreground">
                No commitment rule matches this scope. Arrival Dates will not be committed.
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-4">
        <div className="font-medium">New / edit policy</div>
        <div className="grid grid-cols-4 gap-3">
          <ScopeInput label="Channel (optional)" value={draft.channel}
            onChange={(v) => setDraft({ ...draft, channel: v })} placeholder="shopify" />
          <ScopeInput label="Customer ID (optional)" value={draft.customerId}
            onChange={(v) => setDraft({ ...draft, customerId: v })} placeholder="uuid" />
          <ScopeInput label="Service level (optional)" value={draft.serviceLevel}
            onChange={(v) => setDraft({ ...draft, serviceLevel: v })} placeholder="2_day" />
          <ScopeInput label="Facility (optional)" value={draft.facilityIdInput}
            onChange={(v) => setDraft({ ...draft, facilityIdInput: v })} placeholder="uuid" />
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <Label>Commitment source</Label>
            <Select
              value={draft.commitment_source}
              onValueChange={(v: CommitmentSource) => setDraft({ ...draft, commitment_source: v })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer_required_by">Customer SLA Policy</SelectItem>
                <SelectItem value="channel_promised_by">Channel Policy</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Enforcement</Label>
            <Select
              value={draft.enforcement_mode}
              onValueChange={(v: EnforcementMode) => setDraft({ ...draft, enforcement_mode: v })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off</SelectItem>
                <SelectItem value="warn">Warn</SelectItem>
                <SelectItem value="block">Block</SelectItem>
                <SelectItem value="auto_upgrade">Auto-upgrade</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Grace hours</Label>
            <Input value={draft.grace_hours}
              onChange={(e) => setDraft({ ...draft, grace_hours: e.target.value })} />
          </div>
          <div>
            <Label>Auto-upgrade service</Label>
            <Input value={draft.auto_upgrade_to_service}
              onChange={(e) => setDraft({ ...draft, auto_upgrade_to_service: e.target.value })}
              placeholder="ups_2da" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 items-end">
          <div className="col-span-2">
            <Label>Notes</Label>
            <Input value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch checked={draft.notify_on_miss}
              onCheckedChange={(v) => setDraft({ ...draft, notify_on_miss: v })} />
            <Label className="mb-0">Notify on miss</Label>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch checked={draft.is_active}
              onCheckedChange={(v) => setDraft({ ...draft, is_active: v })} />
            <Label className="mb-0">Active</Label>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save policy
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b text-sm font-medium">Configured policies</div>
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : (rules as any[]).length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            No commitment policies configured. Commitments are not enforced.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">Channel</th>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-left px-4 py-2">Service</th>
                <th className="text-left px-4 py-2">Facility</th>
                <th className="text-left px-4 py-2">Source</th>
                <th className="text-left px-4 py-2">Enforcement</th>
                <th className="text-right px-4 py-2">Grace</th>
                <th className="text-center px-4 py-2">Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(rules as any[]).map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2 font-mono text-xs">{r.channel ?? "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.customer_id ? r.customer_id.slice(0, 8) : "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.service_level ?? "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.facility_id ? r.facility_id.slice(0, 8) : "—"}</td>
                  <td className="px-4 py-2"><Badge variant="outline">{r.commitment_source}</Badge></td>
                  <td className="px-4 py-2"><Badge variant={r.enforcement_mode === "block" ? "destructive" : "secondary"}>{r.enforcement_mode}</Badge></td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.grace_hours}h</td>
                  <td className="px-4 py-2 text-center">{r.is_active ? "Yes" : "No"}</td>
                  <td className="px-4 py-2 text-right">
                    <Button variant="ghost" size="sm"
                      onClick={() => remove.mutate(r.id)} disabled={remove.isPending}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function ScopeInput(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <Label>{props.label}</Label>
      <Input value={props.value} onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder} />
    </div>
  );
}